import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import useInterval from 'shared-components/utils/useInterval';
import { Slide, toast } from 'react-toastify';
import { UserContext } from 'op-contexts';
import { region } from 'op-components';
import { useRouteMatch } from 'react-router-dom';
import { LoadingSpinner } from 'shared-components/components';
import NotesFilter from 'op-components/RO/Notes/NotesFilter';
import { GET_PRACTITIONER } from 'op-graphql/queries';
import { useOnClickOutside } from 'shared-components/utils/CustomHooks';
import { styled } from '@mui/system';
import { ADD_PATIENT_NOTE, UPDATE_PATIENT_NOTE, GET_PATIENT_PROFILE } from '../PatientProfileQueries';
import {
  GET_NOTE_TYPES,
  usePatientNotes,
  PatientNote,
  GET_PATIENT_NOTES,
  NoteType,
} from 'op-components/RO/Notes/Notes';
import { PAT_ID_1_REFRESH_TIME, Region } from 'shared-components/enums';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { formatNoteCreationDate } from 'op-utils/helpers';
import { sanitizeNote } from 'op-pages/RO/Notes/sanitizeNote';
import { Box, Stack, ButtonGroup, Button, useTheme, Typography, Grid, CustomTheme } from '@mui/material';

interface ROPatientNoteNavType {
  id: string;
}

const StyledColNoteMessage = styled(Stack)`
  line-height: 24px;
  color: ${(props) => props.theme.palette.text.primary};
  margin: 8px 0 8px 0;
  word-break: break-word;
  font,
  *,
`;

const StyledColHr = styled(Stack)`
  content: '';
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[300]};
  width: 100%;
`;

export const ToastNotificationMessage = (): JSX.Element => (
  <>
    <strong style={{ maxWidth: '270px', wordWrap: 'normal', overflow: 'hidden' }}>New Note Added</strong>
    <p style={{ maxWidth: '270px', wordWrap: 'normal', overflow: 'hidden', lineHeight: '14px' }}>
      View previous notes via the notes page on the left
    </p>
  </>
);

const ReadPatientNotes = (props: any): JSX.Element => {
  const { notes, loading, filter, practitionerTimezone } = props;
  const theme = useTheme();

  if (loading)
    return (
      <Stack>
        <LoadingSpinner relativeSpinner={true} loadingText={'Loading notes'} />
      </Stack>
    );

  if (!notes && !filter) return <div />;

  const createDate = (note: PatientNote) =>
    formatNoteCreationDate(note, practitionerTimezone, CurrentAppConfig.Notes.DateTimeFormat);

  if (notes && !notes.length) {
    return (
      <Stack paddingTop={1}>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          No notes available
        </Typography>
      </Stack>
    );
  }

  return notes?.map((note: PatientNote) => (
    <Stack marginTop={1} className="notes-note-option" key={note.id}>
      <Stack direction="row" alignContent="center" justifyContent={'space-between'} paddingRight={2}>
        <Typography variant="subtitle2">
          {note.createdBy?.name} {note.practitionerQualification && `(${note.practitionerQualification})`}
        </Typography>
        <Typography color={theme.palette.text.secondary} variant="caption">
          {createDate(note)}
        </Typography>
      </Stack>
      {note.messageTypeAlias && (
        <Typography variant="body2" color={theme.palette.text.secondary} data-test-id="note-heading">
          {note.messageTypeAlias}
        </Typography>
      )}
      <Stack>
        <StyledColNoteMessage
          style={{ whiteSpace: note.formattedMessage.startsWith('<!DOCTYPE html') ? 'normal' : 'pre-wrap' }}>
          <Typography
            variant="body2"
            data-test-id="note-content"
            id="note-content"
            dangerouslySetInnerHTML={sanitizeNote(note.formattedMessage)}
          />
        </StyledColNoteMessage>
      </Stack>
      <Stack>
        <StyledColHr></StyledColHr>
      </Stack>
    </Stack>
  ));
};

const NoteTypeButtons = (props: any): JSX.Element => {
  const { noteTypes, selected, setSelectedNoteType } = props;
  const theme = useTheme();
  const noteTypesCopy = [...noteTypes];

  if (!noteTypes) return <div></div>;
  return (
    <ButtonGroup id="noteTypeButtons" aria-label="Basic example" sx={{ width: 'fit-content' }}>
      {noteTypes &&
        [...noteTypesCopy]
          .sort((a: any, b: any) => a.listWeight - b.listWeight)
          .map((note: any, index: number) => {
            const activeButton = selected === note.name;
            return (
              <Button
                key={index}
                data-test-id={`noteType-${index}`}
                onClick={() => {
                  setSelectedNoteType(note.name);
                }}
                variant="text"
                sx={{
                  textTransform: 'none',
                  color: 'darkslategrey',
                  fontSize: '1rem',
                  borderBottom: activeButton ? `4px solid ${theme.palette.primary.main}` : 'none',
                  paddingBottom: activeButton ? '8px !important' : '12px !important',
                  borderRadius: '0px',
                }}>
                <span>{note.alias ? note.alias : note.name}</span>
              </Button>
            );
          })}
    </ButtonGroup>
  );
};

const ROPatientNotes = (): JSX.Element => {
  const { state, hasFeature } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [selectedNoteType, setSelectedNoteType] = useState<string>('');
  const [draftNote, setDraftNote] = useState<any>();
  const [noteMessage, setNoteMessage] = useState<string>('');
  const match = useRouteMatch<ROPatientNoteNavType>();
  const [timeoutId, setTimeoutId] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { id: patientId } = match.params;
  const theme = useTheme<CustomTheme>();
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([]);

  const { data: pracData } = useQuery(GET_PRACTITIONER, {
    variables: { patientId },
    skip: !patientId,
  });
  const practitionerTimezone = useMemo(
    () => pracData?.practitioner?.timezone || CurrentAppConfig.DefaultTimezone,
    [pracData?.practitioner?.timezone],
  );

  const { notes, filteredNotes, filterNoteTypes, setFilterNoteTypes, isLoading, hasErrors } =
    usePatientNotes(patientId);

  const [updateNoteMutation] = useMutation(UPDATE_PATIENT_NOTE, {
    refetchQueries: [{ query: GET_PATIENT_NOTES, variables: { patientId } }],
  });
  const { data: noteTypesQuery, loading: loadingNoteTypes } = useQuery(GET_NOTE_TYPES, {
    variables: { patientId },
    fetchPolicy: 'cache-and-network',
  });
  const { data: patientProfileData, refetch } = useQuery(GET_PATIENT_PROFILE, {
    variables: { patientId },
  });

  // Refresh profile every few seconds until patient has pat_id_1
  useInterval(
    () => {
      refetch();
    },
    patientProfileData?.patient?.userProfile?.hasPatId1 ? null : PAT_ID_1_REFRESH_TIME,
  );

  useEffect(() => {
    const noteTypesData = noteTypesQuery?.notesTypes;
    if (noteTypesData && noteTypesData.length) {
      setSelectedNoteType(noteTypesData[0].name);
      setNoteTypes(noteTypesData);
    }
  }, [noteTypesQuery]);

  const [addNote] = useMutation(ADD_PATIENT_NOTE, {
    refetchQueries: [{ query: GET_PATIENT_NOTES, variables: { patientId } }],
    onCompleted: (): void => {
      setNoteMessage('');
    },
  });

  useOnClickOutside(containerRef, () => open && setOpen(false));
  useEffect(() => {
    if (notes) {
      const filteredDraft = notes?.filter(
        (note: PatientNote) => note?.isDraft && note?.createdBy?.id === pracData?.practitioner?.id,
      )[0];
      setDraftNote(filteredDraft || null);
      setNoteMessage(filteredDraft?.formattedMessage || '');
      scrollToNewNote();
    }
  }, [notes, pracData?.practitioner?.id]);

  const submitNote = (message: string, noteType: string) => {
    if (message && noteType) {
      addNote({ variables: { patientId, messageType: noteType, message, noteId: draftNote?.id } });
      setNoteMessage('');
      state?.primaryRole === 'ro' &&
        toast.dark(<ToastNotificationMessage />, {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          transition: Slide,
          progress: undefined,
        });
    }
  };

  const scrollToNewNote = () => {
    const notes = document.getElementsByClassName('notes-note-option');
    if (typeof notes[0] !== 'undefined') {
      notes[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const { relatedTarget }: any = e;
    const movingToBlockedTarget =
      relatedTarget?.id === 'add-note-button' || relatedTarget?.dataset?.testId?.startsWith('noteType-');
    if (!movingToBlockedTarget && selectedNoteType) {
      updateNoteMutation({
        variables: { patientId, message: e.target.value, messageType: selectedNoteType, noteId: draftNote?.id },
      });
    }
  };

  const hasPatId1 = useMemo<boolean>(() => patientProfileData?.patient?.userProfile?.hasPatId1, [patientProfileData]);

  const disableNotes = useMemo<boolean>(
    () => !state.hasStaffId || (!hasPatId1 && region !== Region.UK) || noteTypes.length === 0,
    [state, hasPatId1, noteTypes],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} ref={containerRef}>
      <Stack sx={{ height: '100%' }}>
        <Stack sx={{ height: '50%', marginX: '16px' }}>
          <Grid container alignItems="baseline">
            {hasFeature('Next Step') ? (
              <NotesFilter noteTypes={filterNoteTypes} setFilterNoteTypes={setFilterNoteTypes} />
            ) : (
              <Stack>
                <Typography variant="h6" paddingBottom={1}>
                  Notes
                </Typography>
                {notes && notes.length > 0 && (
                  <NotesFilter noteTypes={filterNoteTypes} setFilterNoteTypes={setFilterNoteTypes} />
                )}
              </Stack>
            )}
          </Grid>
          <Stack sx={{ overflowY: 'auto' }} height={1}>
            <ReadPatientNotes
              notes={filteredNotes}
              loading={isLoading}
              error={hasErrors}
              filter={filterNoteTypes}
              practitionerTimezone={practitionerTimezone}
            />
          </Stack>
        </Stack>
        <Stack sx={{ height: '50%' }} borderTop={`1px solid ${theme.palette.grey[300]}`}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            height={'48px'}
            sx={{
              backgroundColor: theme.palette.background.secondary,
              paddingRight: 2,
              width: '100%',
            }}>
            <Typography lineHeight={'48px'} paddingLeft={2} variant="h6">
              Quick notes
            </Typography>
            <Button
              id="add-note-button"
              data-testid="add-note-button"
              onClick={(): void => {
                clearTimeout(timeoutId);
                setTimeoutId(null);
                submitNote(noteMessage, selectedNoteType);
              }}
              disabled={!noteMessage || disableNotes || loadingNoteTypes || selectedNoteType === ''}
              variant="contained">
              Add note
            </Button>
          </Stack>
          {!disableNotes && (
            <Stack sx={{ marginX: '24px' }}>
              <NoteTypeButtons
                noteTypes={noteTypes}
                selected={selectedNoteType}
                setSelectedNoteType={setSelectedNoteType}
              />
            </Stack>
          )}
          {/* Using base textarea here as MUI ones don't autosize width and height to fill component nicely */}
          <textarea
            id="quickNotesInput"
            disabled={disableNotes}
            placeholder={disableNotes ? 'Please add notes in your EMR.' : 'Type your notes here...'}
            value={noteMessage || ''}
            onChange={(e: any): void => {
              setNoteMessage(e.target.value || '');
            }}
            onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
              const timeout = setTimeout(() => handleBlur(event), 400);
              setTimeoutId(timeout);
            }}
            style={{
              maxHeight: '100%',
              maxWidth: '100%',
              height: '100%',
              width: '100%',
              fontFamily: 'unset',
              border: 'unset',
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default ROPatientNotes;
